import React from 'react';
import { Link } from 'react-router-dom';
import { GameCard } from '../features/product/types';
import ResponsiveImage from './ResponsiveImage';
import { hasActiveDiscount, originalPrice } from '../features/product/discount';

const formatReleaseDate = (releaseDate?: string) => {
  if (!releaseDate) return null;
  const parsedDate = new Date(releaseDate);
  if (Number.isNaN(parsedDate.getTime())) return null;
  return new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short' }).format(parsedDate);
};

const formatPrice = (price: number) => new Intl.NumberFormat('ru-RU').format(price);
interface ProductCardProps {
  product: GameCard;
  // Path prefix for the card link. Games -> "/product", services -> "/service".
  hrefBase?: string;
  imageAndNameOnly?: boolean;
}

// Storefront product card, shared by the home feed (carousel) and the favorites grid
// with optional image-and-name presentation. The whole card links to the product page.
const ProductCard: React.FC<ProductCardProps> = ({ product, hrefBase = '/product', imageAndNameOnly = false }) => {
  const oldPrice = originalPrice(product);
  return (
  <Link to={`${hrefBase}/${product.id}`} className="block h-full">
    <article className="group flex h-full min-h-0 flex-col overflow-hidden rounded-[20px] sm:rounded-[28px] border border-foreground/10 bg-[hsl(var(--card-tinted))] transition-all duration-300 hover:-translate-y-1 hover:border-foreground/20">
      <div className="relative aspect-[5/6] w-full overflow-hidden">
        <ResponsiveImage
          src={product.picture}
          // Service artwork is usually a lightweight PNG with flat colours and
          // sharp logo edges. The lossy WebP variants visibly blur those edges,
          // while game covers still benefit substantially from responsive WebP.
          sources={hrefBase === '/service' ? undefined : product.pictureSources}
          sizes="(min-width: 1280px) 240px, (min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw"
          alt={product.name}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          loading="lazy"
          decoding="async"
        />
        {!imageAndNameOnly && formatReleaseDate(product.releaseDate) && (
          <div className="absolute left-2 top-2 sm:left-4 sm:top-4 rounded-full border border-foreground/15 bg-black/45 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-foreground/80 backdrop-blur-sm">
            Новинка
          </div>
        )}
        {!imageAndNameOnly && hasActiveDiscount(product) && (
          <div className="absolute bottom-2 right-2 sm:bottom-3 sm:right-3 rounded-full border border-red-300/40 bg-gradient-to-r from-red-700 to-red-500 px-3 py-1.5 text-sm font-bold text-white shadow-[0_8px_24px_rgba(220,38,38,0.45)] backdrop-blur-sm">
            −{product.discountPercent}%
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col p-2 pb-3 sm:p-4 text-left">
        <div>
          {!imageAndNameOnly && <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
              <div className="whitespace-nowrap text-lg sm:text-xl font-semibold text-foreground">{formatPrice(product.price)} ₽</div>
              {oldPrice && (
                <div className="whitespace-nowrap text-xs sm:text-sm font-medium text-foreground/40 line-through decoration-red-500/70">
                  {formatPrice(oldPrice)} ₽
                </div>
              )}
              {hasActiveDiscount(product) && (
                <span className="shrink-0 rounded-full bg-red-500/15 px-2 py-1 text-[11px] font-bold text-red-600 dark:text-red-400">
                  Скидка {product.discountPercent}%
                </span>
              )}
            </div>
            {formatReleaseDate(product.releaseDate) && (
              <div className="hidden sm:block rounded-full bg-foreground/8 px-3 py-1 text-xs text-foreground/65">
                {formatReleaseDate(product.releaseDate)}
              </div>
            )}
          </div>}

          <h3 className={`${imageAndNameOnly ? '' : 'mt-1 sm:mt-3 '}line-clamp-2 min-h-[2.6em] text-base font-normal leading-[1.3] text-foreground sm:text-[18px]`}>
            {product.name}
          </h3>
        </div>
      </div>
    </article>
  </Link>
  );
};

export default ProductCard;
